import { useIsFocused } from '@react-navigation/native';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { appAgentService } from '../../../services/central-api/app-agent';
import { useAuth } from '../../../context/Auth';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ActivityIndicator, FlatList, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import TeamCard from './TeamCard';
import CreateOseModal from './CreateOseModal';
import Toast from 'react-native-toast-message';
import { ClientError, InvalidValue, UserNotInUnit } from '../../../services/central-api/base-service';
import { translate } from '../../../services/translate';
import OseChecklistModal from './OseChecklistModal';

export default function ExternalServiceOrder() {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getUserTeams'],
        queryFn: () => appAgentService.getUserTeams(),
        enabled: !!authenticatedUser && isFocused,
    });

    const { data, refetch } = queryResult;

    const isEnabled = !!authenticatedUser && isFocused;
    const isLoading = isEnabled && queryResult.isFetching;

    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [createTeam, setCreateTeam] = useState<AppAgentUnit | null>(null);
    const [checklist, setChecklist] = useState<{ teamId: number; oseId: number; } | null>(null);

    const createOse = useMutation({
        mutationFn: ({ unitId, body }: { unitId: number; body: CreateOseBody; }) => {
            return appAgentService.createOse(unitId, body);
        },
        onSuccess: async (_, { unitId }) => {
            setExpandedId(unitId);
            await refetch();
        },
    });

    function getCreateOseErrorMessage(err: unknown): string {
        if (err instanceof UserNotInUnit) {
            return translate('oseUserNotInUnit');
        }

        if (err instanceof InvalidValue) {
            if (err.field === 'driverId') {
                return translate('oseInvalidDriver');
            }
            if (err.field === 'personInChargeId') {
                return translate('oseInvalidPersonInCharge');
            }
            if (err.field === 'startFuelLevel') {
                return translate('oseInvalidStartFuelLevel');
            }
        }

        if (err instanceof ClientError) {
            if (err.message === 'OseAlreadyOpen') {
                return translate('oseAlreadyOpen');
            }
            if (err.message === 'CategoryDoesNotGenerateOse') {
                return translate('oseCategoryDoesNotGenerate');
            }
        }

        return translate('oseCreateError');
    }

    return (
        <SafeAreaView style={styles.container}>
            {isLoading
                ? <View style={styles.center}>
                    <ActivityIndicator size='large' color={theme.colors.iconColor} />
                </View>
                : <FlatList
                    data={data ?? []}
                    keyExtractor={(item) => {
                        return `user_unit_${String(item.id)}`;
                    }}
                    style={styles.flatList}
                    contentContainerStyle={styles.list}
                    renderItem={({ item }) => (
                        <TeamCard
                            team={item}
                            expanded={expandedId === item.id}
                            onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                            onCreateOse={() => setCreateTeam(item)}
                            onOpenOse={(ose) => setChecklist({ teamId: item.id, oseId: ose.id })}
                        />
                    )}
                    showsVerticalScrollIndicator={false}
                    ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
                />}
            <CreateOseModal
                team={createTeam}
                visible={!!createTeam}
                setVisible={(value) => {
                    if (!value) {
                        setCreateTeam(null);
                    }
                }}
                isSubmitting={createOse.isPending}
                onSubmit={async (data) => {
                    if (!createTeam || data.driverId == null || data.personInChargeId == null) {
                        return false;
                    }
                    try {
                        await createOse.mutateAsync({
                            unitId: createTeam.id,
                            body: { ...data, driverId: data.driverId, personInChargeId: data.personInChargeId },
                        });
                        Toast.show({
                            type: 'sentinelxSuccess',
                            text1: translate('ActionSuccessfully')
                        });
                        return true;
                    } catch (err) {
                        Toast.show({
                            type: 'sentinelxError',
                            text1: getCreateOseErrorMessage(err)
                        });
                        return false;
                    }
                }}
            />
            <OseChecklistModal
                team={data?.find((t) => t.id === checklist?.teamId) ?? null}
                oseId={checklist?.oseId ?? null}
                visible={!!checklist}
                setVisible={(value) => {
                    if (!value) {
                        setChecklist(null);
                    }
                }}
                onChanged={refetch}
            />
        </SafeAreaView>
    );
}

const styleSheet = createStyleSheet(() => ({
    list: {
        paddingTop: 16,
        paddingBottom: 96,
        paddingHorizontal: 16
    },
    container: {
        flex: 1,
    },
    center: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    flatList: {
        flex: 1,
    },
}));
